import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-hot-toast';
import { usePermissions } from '../../hooks/usePermissions';
import { 
  Loader2, 
  AlertTriangle, 
  X, 
  Download,
  Database,
  Info,
  Server,
  FileText,
  Check,
  ChevronDown,
  PlusCircle,
  Trash2
} from 'lucide-react';
import { 
  Product, 
  Tenant, 
  LoggerCustomer,
  LoggerProduct,
  LoggerKeyword
} from '../../types';
import { 
  getLoggerCustomer, 
  createLoggerCustomer, 
  updateLoggerCustomer,
  generateConfigFile,
  updateLoggerIntegrationSettings,
  updateLoggerCustomerStatus
} from '../../services/api';
import { getLoggerProducts } from '../../services/loggerApi';
import { downloadConfigFile } from '../../utils/ConfigFile.js';

interface LoggerConfigModalProps {
  product: Product;
  organization: Tenant;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

// Tooltip component
const Tooltip: React.FC<{ content: string }> = ({ content }) => {
  return (
    <span className="ml-1 group relative inline-block">
      <Info size={14} className="text-gray-400 cursor-help" />
      <div className="opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 p-2 bg-gray-800 text-xs text-white rounded shadow-lg z-50">
        {content}
        <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 rotate-45 bg-gray-800"></div>
      </div>
    </span>
  );
};

const LoggerConfigModal: React.FC<LoggerConfigModalProps> = ({
  product,
  organization,
  isOpen,
  onClose,
  onSaved
}) => {
  const { canManageIntegrations } = usePermissions();
  const [existingCustomer, setExistingCustomer] = useState<LoggerCustomer | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableProducts, setAvailableProducts] = useState<LoggerProduct[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [isProductsDropdownOpen, setIsProductsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [productsForDownload, setProductsForDownload] = useState<string[]>([]);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  
  // Check permissions
  useEffect(() => {
    if (isOpen && !canManageIntegrations()) {
      onClose();
    }
  }, [isOpen, canManageIntegrations, onClose]);

  // Customer Information fields
  const [customerInfo, setCustomerInfo] = useState({
    target: '',
    logger_type: 'Logger 01',
    status: 'active' as 'active' | 'inactive',
    max_data_size: '100' // Default to 100GB
  });

  // TLS Certificate Configuration fields
  const [tlsConfig, setTlsConfig] = useState({
    cacert: '',
    mycert: '',
    myprivkey: '',
    permittedpeer: ''
  });

  const [, setKeywords] = useState<LoggerKeyword[]>([]);
  const [newKeyword, setNewKeyword] = useState<string>('');
  const [newCondition, setNewCondition] = useState<'NONE' | 'AND' | 'OR'>('NONE');
  const [productKeywords, setProductKeywords] = useState<Record<string, LoggerKeyword[]>>({});
  const [activeProductId, setActiveProductId] = useState<string | null>(null);
  
  // Device ID management
  const [productDeviceIds, setProductDeviceIds] = useState<Record<string, string[]>>({});
  const [newDeviceId, setNewDeviceId] = useState<string>('');

  // Fetch existing logger customer data if it exists
  useEffect(() => {
    const fetchExistingCustomer = async () => {
      if (!isOpen || !organization || !product) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        // First check if this organization already has a logger customer with this product
        const customer = await getLoggerCustomer(organization.id);
        
        if (customer) {
          // Use as any to bypass TypeScript checks since we know the structure is correct
          const customerData = customer as any;
          setExistingCustomer(customerData);
          setSelectedProducts(customerData.logger_products || []);
          
          // Populate customer info fields
          setCustomerInfo({
            target: customerData.target || '',
            logger_type: customerData.logger_type || 'Logger 01',
            status: customerData.status || 'active',
            max_data_size: customerData.max_data_size || '100'
          });

          // Populate TLS config fields
          if (customerData.tls_config) {
            setTlsConfig({
              cacert: customerData.tls_config.cacert || '',
              mycert: customerData.tls_config.mycert || '',
              myprivkey: customerData.tls_config.myprivkey || '',
              permittedpeer: customerData.tls_config.permittedpeer || ''
            });
          }
          
          // Populate keywords and device IDs per product if available
          const loggerProductIds: string[] = customerData.logger_products || [];
          if (customerData.product_data) {
            const keywordsMap: Record<string, LoggerKeyword[]> = {};
            const deviceIdsMap: Record<string, string[]> = {};

            // Initialize with empty arrays for all products
            loggerProductIds.forEach(productId => {
              keywordsMap[productId] = [];
              deviceIdsMap[productId] = [];
            });

            // Populate with existing data
            Object.keys(customerData.product_data).forEach(productId => {
              const productData = customerData.product_data[productId];
              if (productData.keywords) {
                keywordsMap[productId] = productData.keywords;
              }
              if (productData.device_ids) {
                deviceIdsMap[productId] = productData.device_ids;
              }
            });

            setProductKeywords(keywordsMap);
            setProductDeviceIds(deviceIdsMap);
          } else {
            // Fallback to global keywords/device IDs (for backward compatibility)
            if (customerData.keywords) {
              setKeywords(customerData.keywords || []);
            }

            const deviceIdsMap: Record<string, string[]> = {};
            loggerProductIds.forEach(productId => {
              deviceIdsMap[productId] = customerData.device_ids || [];
            });
            setProductDeviceIds(deviceIdsMap);
          }
        } else {
          setExistingCustomer(null);
          setSelectedProducts([product.id as string]); // Default to current product
        }
      } catch (err) {
        console.error('Error fetching logger customer:', err);
        setError('Failed to load existing logger configuration. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchExistingCustomer();
  }, [isOpen, organization, product]);

  // Fetch available logger products
  useEffect(() => {
    const fetchProducts = async () => {
      if (!isOpen) return;
      
      setProductsLoading(true);
      
      try {
        const products = await getLoggerProducts();
        setAvailableProducts(products);

        // Only initialize maps for products not already loaded from existing customer data
        setProductKeywords(prev => {
          const keywordsMap: Record<string, LoggerKeyword[]> = { ...prev };
          products.forEach(p => {
            const id = p.id as string;
            if (!(id in keywordsMap)) {
              keywordsMap[id] = p.keywords?.length ? p.keywords : [];
            }
          });
          return keywordsMap;
        });
        setProductDeviceIds(prev => {
          const deviceIdsMap: Record<string, string[]> = { ...prev };
          products.forEach(p => {
            const id = p.id as string;
            if (!(id in deviceIdsMap)) {
              deviceIdsMap[id] = [];
            }
          });
          return deviceIdsMap;
        });

        // Set the first product as active if available
        if (products.length > 0 && products[0].id) {
          setActiveProductId(prev => prev ?? (products[0].id as string));
        }
      } catch (error) {
        console.error('Error fetching logger products:', error);
        toast.error('Failed to load logger products');
      } finally {
        setProductsLoading(false);
      }
    };
    
    fetchProducts();
  }, [isOpen]);

  // Update selected products when they change
  useEffect(() => {
    if (selectedProducts.length > 0 && !activeProductId) {
      setActiveProductId(selectedProducts[0]);
    }
  }, [selectedProducts, activeProductId]);

  // Handle product selection/deselection
  const handleProductToggle = (productId: string) => {
    setSelectedProducts((prev) => {
      if (prev.includes(productId)) {
        return prev.filter((id) => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  // Handle customer info field changes
  const handleCustomerInfoChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Add validation for max_data_size
    if (name === 'max_data_size') {
      const numValue = parseFloat(value);
      if (isNaN(numValue)) {
        toast.error('Please enter a valid number for data size');
        return;
      }
      
      if (numValue > 100) {
        toast.error('Maximum data size cannot exceed 100GB. For larger sizes, please create multiple logger configurations.');
        return;
      }
    }
    
    setCustomerInfo(prev => ({ ...prev, [name]: value }));
  };

  // Handle TLS config field changes
  const handleTlsConfigChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTlsConfig(prev => ({ ...prev, [name]: value }));
  };

  // Handle keyword addition
  const handleAddKeyword = (productId: string) => {
    if (newKeyword.trim()) {
      // Update the keywords for the specific product
      setProductKeywords(prev => {
        const updatedKeywords = [...(prev[productId] || [])];
        updatedKeywords.push({
          keyword: newKeyword.trim(),
          condition: updatedKeywords.length > 0 ? (newCondition === 'NONE' ? 'AND' : newCondition) : ''
        });
        
        return {
          ...prev,
          [productId]: updatedKeywords
        };
      });
      
      setNewKeyword('');
      setNewCondition('NONE');
    } else {
      toast.error('Keyword cannot be empty');
    }
  };

  // Handle keyword removal
  const handleRemoveKeyword = (productId: string, index: number) => {
    setProductKeywords(prev => {
      const updatedKeywords = [...(prev[productId] || [])];
      updatedKeywords.splice(index, 1);
      
      return {
        ...prev,
        [productId]: updatedKeywords
      };
    });
  };

  // Handle device ID addition
  const handleAddDeviceId = (productId: string) => {
    if (newDeviceId.trim()) {
      setProductDeviceIds(prev => {
        const updatedDeviceIds = [...(prev[productId] || [])];
        if (!updatedDeviceIds.includes(newDeviceId.trim())) {
          updatedDeviceIds.push(newDeviceId.trim());
        }
        
        return {
          ...prev,
          [productId]: updatedDeviceIds
        };
      });
      
      setNewDeviceId('');
    } else {
      toast.error('Device ID cannot be empty');
    }
  };

  // Handle device ID removal
  const handleRemoveDeviceId = (productId: string, index: number) => {
    setProductDeviceIds(prev => {
      const updatedDeviceIds = [...(prev[productId] || [])];
      updatedDeviceIds.splice(index, 1);
      
      return {
        ...prev,
        [productId]: updatedDeviceIds
      };
    });
  };

  // Update keywords before submission
  const prepareKeywordsForSubmission = () => {
    // For each product, prepare its own keywords
    const productSpecificKeywords: Record<string, LoggerKeyword[]> = {};
    
    // Ensure each product has its own keywords
    selectedProducts.forEach(productId => {
      // Get the keywords for this product
      const keywords = productKeywords[productId] || [];
      
      // Store the keywords for this product
      productSpecificKeywords[productId] = keywords;
    });
    
    return productSpecificKeywords;
  };

  // Prepare device IDs for submission
  const prepareDeviceIdsForSubmission = () => {
    // For each product, prepare its own device IDs
    const productSpecificDeviceIds: Record<string, string[]> = {};
    
    // Ensure each product has its own device IDs
    selectedProducts.forEach(productId => {
      // Get the device IDs for this product
      const deviceIds = productDeviceIds[productId] || [];
      
      // Store the device IDs for this product
      productSpecificDeviceIds[productId] = deviceIds;
    });
    
    return productSpecificDeviceIds;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (selectedProducts.length === 0) {
      toast.error('Please select at least one logger product');
      return;
    }

    // Validate required fields
    if (!customerInfo.target) {
      toast.error('Target field is required');
      return;
    }
    
    // Validate max_data_size
    const maxDataSize = parseFloat(customerInfo.max_data_size);
    if (isNaN(maxDataSize) || maxDataSize <= 0) {
      toast.error('Please enter a valid positive number for maximum data size');
      return;
    }
    
    if (maxDataSize > 100) {
      toast.error('Maximum data size cannot exceed 100GB. For larger sizes, please create multiple logger configurations.');
      return;
    }

    // Prepare product-specific keywords and device IDs
    const productSpecificKeywords = prepareKeywordsForSubmission();
    const productSpecificDeviceIds = prepareDeviceIdsForSubmission();

    setIsSubmitting(true);
    setError(null);
    
    try {
      // Process each product with its own keywords and device IDs
      for (const productId of selectedProducts) {
        // Get the keywords and device IDs for this product
        const keywords = productSpecificKeywords[productId] || [];
        const deviceIds = productSpecificDeviceIds[productId] || [];
        
        // Create a properly structured object with all necessary fields
        const formData = {
          customer_id: organization.id,
          target: customerInfo.target,
          logger_type: customerInfo.logger_type || 'Logger 01',
          status: customerInfo.status || 'active',
          max_data_size: customerInfo.max_data_size,
          logger_products: [productId], // Only include the current product
          // Include keywords specific to this product
          keywords: keywords,
          // Include device IDs specific to this product
          device_ids: deviceIds,
          // Include TLS config fields
          tls_cacert: tlsConfig.cacert || '',
          tls_mycert: tlsConfig.mycert || '',
          tls_myprivkey: tlsConfig.myprivkey || '',
          tls_permittedpeer: tlsConfig.permittedpeer || ''
        };
        
        // Submitting logger configuration
        
        // Update the integration settings for this product
        await updateLoggerIntegrationSettings(formData);
      }
      
      // Also need to deactivate any products that were removed
      if (existingCustomer?.logger_products) {
        const removedProducts = existingCustomer.logger_products.filter(
          (id: string) => !selectedProducts.includes(id)
        );
        
        // Deactivate each removed product
        for (const productId of removedProducts) {
          // Add type annotation to help TypeScript
          const deactivateData: {
            customer_id: string;
            product_id: string;
            is_active: boolean;
          } = {
            customer_id: organization.id,
            product_id: productId,
            is_active: false
          };
          
          try {
            await updateLoggerCustomerStatus(deactivateData);
            // Product deactivated
          } catch (error) {
            console.error(`Failed to deactivate product ${productId}:`, error);
          }
        }
      }
      
      let response;
      
      // Update or create logger customer
      if (existingCustomer?.id) {
        try {
          // Create a properly structured object with all necessary fields for updateLoggerCustomer
          const customerFormData = {
            customer_id: organization.id,
            target: customerInfo.target,
            logger_type: customerInfo.logger_type || 'Logger 01',
            status: customerInfo.status || 'active',
            max_data_size: customerInfo.max_data_size,
            logger_products: selectedProducts,
            // Include TLS config
            tls_config: {
              cacert: tlsConfig.cacert || '',
              mycert: tlsConfig.mycert || '',
              myprivkey: tlsConfig.myprivkey || '',
              permittedpeer: tlsConfig.permittedpeer || ''
            }
          };
          
          response = await updateLoggerCustomer(existingCustomer.id, customerFormData as any);
          
          if (!response) {
            throw new Error('Failed to update logger configuration');
          }
          
        toast.success('Logger configuration updated successfully');
        } catch (updateError: any) {
          console.error('Error updating logger customer:', updateError);
          
          // If update fails, try creating a new one directly
          const customerFormData = {
            customer_id: organization.id,
            target: customerInfo.target,
            logger_type: customerInfo.logger_type || 'Logger 01',
            status: customerInfo.status || 'active',
            max_data_size: customerInfo.max_data_size,
            logger_products: selectedProducts,
            // Include TLS config
            tls_config: {
              cacert: tlsConfig.cacert || '',
              mycert: tlsConfig.mycert || '',
              myprivkey: tlsConfig.myprivkey || '',
              permittedpeer: tlsConfig.permittedpeer || ''
            }
          };
          
          response = await createLoggerCustomer(customerFormData as any);
          
          if (!response) {
            throw new Error('Failed to create logger configuration after update failed');
          }
          
          toast.success('Created new logger configuration successfully');
        }
      } else {
        // Create a properly structured object with all necessary fields for createLoggerCustomer
        const customerFormData = {
          customer_id: organization.id,
          target: customerInfo.target,
          logger_type: customerInfo.logger_type || 'Logger 01',
          status: customerInfo.status || 'active',
          max_data_size: customerInfo.max_data_size,
          logger_products: selectedProducts,
          // Include TLS config
          tls_config: {
            cacert: tlsConfig.cacert || '',
            mycert: tlsConfig.mycert || '',
            myprivkey: tlsConfig.myprivkey || '',
            permittedpeer: tlsConfig.permittedpeer || ''
          }
        };
        
        response = await createLoggerCustomer(customerFormData as any);
        
        if (!response) {
          throw new Error('Failed to create logger configuration');
        }
        
        toast.success('Logger configuration created successfully');
      }
      
      // Notify parent component to refresh data
      onSaved();
      
      // Close the modal
      onClose();
    } catch (err: any) {
      console.error('Error saving logger configuration:', err);
      const errorMessage = err.message || 'Failed to save logger configuration. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle configuration file download
  const handleDownloadConfig = async () => {
    if (!organization.id) {
      toast.error('Customer ID is required');
      return;
    }
    
    // Select all products by default when opening the download modal
    setProductsForDownload([...selectedProducts]);
    
    // Use a small timeout to prevent any race conditions with click events
    setTimeout(() => {
      // Open the download modal to select products
      setIsDownloadModalOpen(true);
    }, 100);
  };
  
  // Handle download for specific products
  const handleDownloadSpecificConfig = async () => {
    if (productsForDownload.length === 0) {
      toast.error('Please select at least one product for download');
      return;
    }
    
    setIsDownloading(true);
    
    try {
      // Get the logger type - default to 'logger01' if not specified
      const loggerType = existingCustomer?.logger_type || 'logger01';
      
      // Generate and download the config file with specific products
      const config = await generateConfigFile(
        organization.id, 
        loggerType,
        productsForDownload.join(',')
      );
      
      // Download the file
      const productSuffix = productsForDownload.length === selectedProducts.length 
        ? '' 
        : `_${productsForDownload.length}_products`;
      
      downloadConfigFile(
        config.template_content,
        `${organization.name.replace(/\s+/g, '_').toLowerCase()}${productSuffix}_rsyslog.conf`
      );
      
      toast.success('Configuration file downloaded');
      setIsDownloadModalOpen(false);
    } catch (error) {
      console.error('Error generating config file:', error);
      toast.error('Failed to generate configuration file');
    } finally {
      setIsDownloading(false);
    }
  };
  
  // Render download modal
  const renderDownloadModal = () => {
    if (!isDownloadModalOpen) return null;
    
    return createPortal(
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[200] flex items-center justify-center p-4"
        onClick={() => setIsDownloadModalOpen(false)}
      >
        <div 
          className="bg-background-card rounded-lg shadow-xl w-full max-w-lg animate-modalOpen"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-4 border-b border-border-dark">
            <h3 className="text-lg font-semibold">Download Configuration</h3>
            <button 
              onClick={(e) => {
                e.stopPropagation();
                setIsDownloadModalOpen(false);
              }}
              className="p-1 rounded-full hover:bg-background-hover text-gray-400 hover:text-gray-200"
            >
              <X size={20} />
            </button>
          </div>
          
          <div className="p-5">
            <p className="text-sm text-gray-300 mb-4">
              Select the products you want to include in the configuration file:
            </p>
            
            <div className="space-y-2 max-h-60 overflow-y-auto mb-4">
              {selectedProducts.map(productId => {
                const product = availableProducts.find(p => p.id === productId);
                return (
                  <div key={productId} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`download-${productId}`}
                      checked={productsForDownload.includes(productId)}
                      onChange={(e) => {
                        e.stopPropagation();
                        if (e.target.checked) {
                          setProductsForDownload(prev => [...prev, productId]);
                        } else {
                          setProductsForDownload(prev => prev.filter(id => id !== productId));
                        }
                      }}
                      className="mr-2 h-4 w-4 rounded border-gray-600 bg-gray-800 text-primary"
                    />
                    <label 
                      htmlFor={`download-${productId}`} 
                      className="text-sm text-gray-300"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {product?.product_name || 'Unknown Product'}
                    </label>
                  </div>
                );
              })}
            </div>
            
            <div className="flex justify-between mt-4">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsDownloadModalOpen(false);
                }}
                className="px-4 py-2 border border-gray-600 text-gray-300 rounded-md hover:bg-gray-700"
              >
                Cancel
              </button>
              
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownloadSpecificConfig();
                }}
                disabled={isDownloading || productsForDownload.length === 0}
                className={`flex items-center px-4 py-2 rounded-md text-white ${
                  productsForDownload.length > 0 ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-600 cursor-not-allowed'
                }`}
              >
                {isDownloading ? (
                  <Loader2 size={16} className="animate-spin mr-2" />
                ) : (
                  <Download size={16} className="mr-2" />
                )}
                Download Selected ({productsForDownload.length})
              </button>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  // Render the logger products section with dropdown
  const renderLoggerProductsSection = () => {
    return (
      <div className="mb-6">
        <div className="bg-background-card border border-card-border rounded-lg overflow-hidden">
          <div className="bg-slate-800/30 px-4 py-3 font-medium text-gray-300 flex items-center">
            <Database size={16} className="mr-2 text-primary" />
            Logger Products
          </div>
          <div className="p-5 space-y-4">
            {productsLoading ? (
              <div className="text-text-muted text-center py-4">
                <Loader2 size={20} className="animate-spin mx-auto mb-2" />
                Loading products...
              </div>
            ) : availableProducts.length === 0 ? (
              <div className="text-text-muted text-center py-4">
                No logger products available
              </div>
            ) : (
              <div>
                <label className="text-sm font-medium text-gray-300 mb-1.5 block">
                  Select Logger Products <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  {/* Dropdown trigger */}
                  <div 
                    ref={dropdownRef}
                    className="w-full bg-gray-800/70 border border-gray-700 rounded-md px-3 py-2.5 min-h-10 cursor-pointer flex items-center justify-between dropdown-container"
                    onClick={() => {
                      if (dropdownRef.current) {
                        const rect = dropdownRef.current.getBoundingClientRect();
                        setDropdownPosition({
                          top: rect.bottom + window.scrollY,
                          left: rect.left + window.scrollX,
                          width: rect.width
                        });
                      }
                      setIsProductsDropdownOpen(!isProductsDropdownOpen);
                    }}
                  >
                    <div className="flex flex-wrap gap-2 items-center">
                      {selectedProducts.length === 0 ? (
                        <span className="text-gray-400 text-sm">Select logger products...</span>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {selectedProducts.map(productId => {
                            const productInfo = availableProducts.find(p => p.id === productId);
                            return productInfo ? (
                              <span key={productId} className="bg-primary-700/30 text-primary-300 px-2 py-0.5 rounded text-xs">
                                {productInfo.product_name}
                              </span>
                            ) : null;
                          })}
                        </div>
                      )}
                    </div>
                    <ChevronDown 
                      size={16} 
                      className={`transition-transform duration-200 ${isProductsDropdownOpen ? 'rotate-180' : ''}`} 
                    />
                  </div>
                  
                  {/* Dropdown menu - rendered in portal */}
                  {isProductsDropdownOpen && createPortal(
                    <div 
                      className="fixed z-[9999] bg-gray-800 border border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto dropdown-container"
                      style={{
                        top: `${dropdownPosition.top + 5}px`,
                        left: `${dropdownPosition.left}px`,
                        width: `${dropdownPosition.width}px`
                      }}
                    >
                      {availableProducts.map((loggerProduct) => (
                        <div
                          key={loggerProduct.id}
                          className={`px-3 py-2 cursor-pointer hover:bg-gray-700 ${
                            selectedProducts.includes(loggerProduct.id || '') 
                              ? 'bg-primary-700/50 text-primary-200' 
                              : 'text-white'
                          }`}
                          onClick={() => {
                            if (loggerProduct.id) {
                              handleProductToggle(loggerProduct.id);
                            }
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <div className="h-8 w-8 mr-2 flex-shrink-0 flex items-center justify-center bg-gray-700 rounded">
                                <span className="text-xs font-bold text-gray-300">
                                  {loggerProduct.product_name.substring(0, 2).toUpperCase()}
                                </span>
                              </div>
                              <div>
                                <div className="font-medium">{loggerProduct.product_name}</div>
                                <div className="text-xs text-text-muted-dark">
                                  {loggerProduct.product_type} - {loggerProduct.log_type}
                                </div>
                              </div>
                            </div>
                            {selectedProducts.includes(loggerProduct.id || '') && (
                              <Check size={16} className="text-primary-300" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>,
                    document.body
                  )}
                </div>
                
                {selectedProducts.length === 0 && (
                  <p className="text-status-danger text-sm mt-2">Please select at least one logger product</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render the customer information section
  const renderCustomerInformationSection = () => {
    return (
      <div className="mb-6">
        <div className="bg-background-card border border-card-border rounded-lg overflow-hidden">
          <div className="bg-slate-800/30 px-4 py-3 font-medium text-gray-300 flex items-center">
            <Server size={16} className="mr-2 text-primary" />
            Customer Information
          </div>
          <div className="p-5 space-y-4">
            <div className="form-group">
              <label className="text-sm font-medium text-gray-300 mb-1.5 block">
                Customer
              </label>
              <div className="bg-gray-800/70 border border-gray-700 rounded-md px-3 py-2.5 text-sm flex justify-between items-center">
                <span>{organization.name}</span>
              </div>
              <p className="text-xs text-gray-400 mt-1">This customer is pre-selected based on the integration context.</p>
            </div>

            <div className="form-group">
              <label className="text-sm font-medium text-gray-300 mb-1.5 block">
                Target <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                name="target"
                value={customerInfo.target}
                onChange={handleCustomerInfoChange}
                className="w-full bg-gray-800/70 border border-gray-700 rounded-md px-3 py-2.5 text-sm h-10 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Enter target IP or hostname"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="form-group">
                <label className="text-sm font-medium text-gray-300 mb-1.5 block">
                  Logger Type <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="logger_type"
                  value={customerInfo.logger_type}
                  onChange={handleCustomerInfoChange}
                  className="w-full bg-gray-800/70 border border-gray-700 rounded-md px-3 py-2.5 text-sm h-10 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Enter logger type"
                  required
                />
              </div>

              <div className="form-group">
                <label className="text-sm font-medium text-gray-300 mb-1.5 block">
                  Max Data Size (GB) <span className="text-red-400">*</span>
                  <Tooltip content="Maximum total data size in gigabytes. Cannot exceed 100GB. For larger sizes, create multiple configurations." />
                </label>
                <input
                  type="number"
                  name="max_data_size"
                  value={customerInfo.max_data_size}
                  onChange={handleCustomerInfoChange}
                  className="w-full bg-gray-800/70 border border-gray-700 rounded-md px-3 py-2.5 text-sm h-10 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Enter maximum data size in GB"
                  min="1"
                  max="100"
                  required
                />
                <p className="text-xs text-gray-400 mt-1">Maximum allowed: 100GB</p>
              </div>

              <div className="form-group">
                <label className="text-sm font-medium text-gray-300 mb-1.5 block">
                  Status <span className="text-red-400">*</span>
                </label>
                <select
                  name="status"
                  value={customerInfo.status}
                  onChange={handleCustomerInfoChange}
                  className="w-full bg-gray-800/70 border border-gray-700 rounded-md px-3 py-2.5 text-sm h-10 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render the TLS certificate configuration section
  const renderTlsCertificateSection = () => {
    return (
      <div className="mb-6">
        <div className="bg-background-card border border-card-border rounded-lg overflow-hidden">
          <div className="bg-slate-800/30 px-4 py-3 font-medium text-gray-300 flex items-center">
            <FileText size={16} className="mr-2 text-primary" />
            TLS Certificate Configuration
          </div>
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="form-group">
                <label className="text-sm font-medium text-gray-300 mb-1.5 block">
                  CA Certificate Path
                </label>
                <input
                  type="text"
                  name="cacert"
                  value={tlsConfig.cacert}
                  onChange={handleTlsConfigChange}
                  className="w-full bg-gray-800/70 border border-gray-700 rounded-md px-3 py-2.5 text-sm h-10 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="/etc/ssl/rsyslog/CA.pem"
                />
                <p className="text-xs text-gray-400 mt-1">Path to the CA certificate file on the client machine</p>
              </div>

              <div className="form-group">
                <label className="text-sm font-medium text-gray-300 mb-1.5 block">
                  Client Certificate Path
                </label>
                <input
                  type="text"
                  name="mycert"
                  value={tlsConfig.mycert}
                  onChange={handleTlsConfigChange}
                  className="w-full bg-gray-800/70 border border-gray-700 rounded-md px-3 py-2.5 text-sm h-10 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="/etc/ssl/rsyslog/client-cert.pem"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="form-group">
                <label className="text-sm font-medium text-gray-300 mb-1.5 block">
                  Client Private Key Path
                </label>
                <input
                  type="text"
                  name="myprivkey"
                  value={tlsConfig.myprivkey}
                  onChange={handleTlsConfigChange}
                  className="w-full bg-gray-800/70 border border-gray-700 rounded-md px-3 py-2.5 text-sm h-10 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="/etc/ssl/rsyslog/client-key.pem"
                />
              </div>

              <div className="form-group">
                <label className="text-sm font-medium text-gray-300 mb-1.5 block">
                  Permitted Peer
                </label>
                <input
                  type="text"
                  name="permittedpeer"
                  value={tlsConfig.permittedpeer}
                  onChange={handleTlsConfigChange}
                  className="w-full bg-gray-800/70 border border-gray-700 rounded-md px-3 py-2.5 text-sm h-10 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="*.example.com"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render the Device IDs section
  const renderDeviceIdsSection = () => {
    const getProductNameById = (productId: string) => {
      const foundProduct = availableProducts.find(p => p.id === productId);
      return foundProduct ? foundProduct.product_name : 'Unknown Product';
    };

    return (
      <div className="mb-6">
        <div className="bg-background-card border border-card-border rounded-lg overflow-hidden">
          <div className="bg-slate-800/30 px-4 py-3 font-medium text-gray-300 flex items-center">
            <Server size={16} className="mr-2 text-primary" />
            Device IDs
            <Tooltip content="Add device IDs for each selected product. Device IDs will be used to identify specific devices for logging." />
          </div>
          <div className="p-5">
            {selectedProducts.length === 0 ? (
              <div className="text-gray-400 text-center py-4">
                Please select at least one product to add device IDs.
              </div>
            ) : (
              <>
                {/* Product tabs */}
                <div className="flex flex-wrap gap-2 mb-4 border-b border-gray-700">
                  {selectedProducts.map(productId => (
                    <button
                      key={productId}
                      className={`px-4 py-2 rounded-t-md text-sm font-medium ${
                        activeProductId === productId 
                          ? 'bg-primary-700/30 text-primary-300 border-b-2 border-primary-500' 
                          : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800/50'
                      }`}
                      onClick={() => setActiveProductId(productId)}
                    >
                      {getProductNameById(productId)}
                    </button>
                  ))}
                </div>
                
                {/* Active product device IDs section */}
                {activeProductId && (
                  <div className="space-y-4">
                    <div className="mb-4">
                      <div className="flex items-end space-x-2">
                        <div className="flex-grow">
                          <label className="block text-sm font-medium mb-1 text-gray-300">Device ID</label>
                          <input
                            type="text"
                            value={newDeviceId}
                            onChange={(e) => setNewDeviceId(e.target.value)}
                            className="w-full bg-gray-800/70 border border-gray-700 rounded-md px-3 py-2.5 text-sm h-10 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder="Enter device ID"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter' && newDeviceId.trim()) {
                                e.preventDefault();
                                handleAddDeviceId(activeProductId);
                              }
                            }}
                          />
                        </div>
                        <div className="flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => handleAddDeviceId(activeProductId)}
                            className="h-10 px-4 bg-primary hover:bg-primary-dark text-white rounded-md transition-colors flex items-center"
                          >
                            <PlusCircle size={16} className="mr-1" />
                            Add
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Display existing device IDs for the active product */}
                    {productDeviceIds[activeProductId] && productDeviceIds[activeProductId].length > 0 ? (
                      <div className="space-y-2">
                        {productDeviceIds[activeProductId].map((deviceId, index) => (
                          <div key={index} className="flex items-center justify-between bg-gray-800/50 p-3 rounded-md">
                            <div className="flex items-center">
                              <Server size={16} className="mr-2 text-primary-400" />
                              <span className="font-medium text-gray-300">{deviceId}</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveDeviceId(activeProductId, index)}
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-gray-400 text-center py-4">
                        No device IDs added for this product. Device IDs will be used to identify specific devices for logging.
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render the Keywords And/Or condition block section
  const renderKeywordsSection = () => {
    // Generate a readable preview of the condition structure for a specific product
    const generateConditionPreview = (productId: string) => {
      const productKeywordsList = productKeywords[productId] || [];
      if (productKeywordsList.length === 0) return '';
      
      let preview = '';
      let currentCondition = '';
      
      productKeywordsList.forEach((kw, index) => {
        if (index === 0) {
          preview = `if ($msg contains "${kw.keyword}")`;
        } else {
          currentCondition = kw.condition || 'AND'; // Default to AND if condition is empty
          preview += ` ${currentCondition.toLowerCase()} ($msg contains "${kw.keyword}")`;
        }
      });
      
      return preview + ' then process log';
    };

    // Find product name by ID
    const getProductNameById = (productId: string) => {
      const foundProduct = availableProducts.find(p => p.id === productId);
      return foundProduct ? foundProduct.product_name : 'Unknown Product';
    };

    return (
      <div className="mb-6">
        <div className="bg-background-card border border-card-border rounded-lg overflow-hidden">
          <div className="bg-slate-800/30 px-4 py-3 font-medium text-gray-300 flex items-center">
            <Database size={16} className="mr-2 text-primary" />
            Keywords And/Or Conditions
            <Tooltip content="Add keywords for each selected product. Keywords will be used to filter logs." />
          </div>
          <div className="p-5">
            {selectedProducts.length === 0 ? (
              <div className="text-gray-400 text-center py-4">
                Please select at least one product to add keywords.
              </div>
            ) : (
              <>
                {/* Product tabs */}
                <div className="flex flex-wrap gap-2 mb-4 border-b border-gray-700">
                  {selectedProducts.map(productId => (
                    <button
                      key={productId}
                      className={`px-4 py-2 rounded-t-md text-sm font-medium ${
                        activeProductId === productId 
                          ? 'bg-primary-700/30 text-primary-300 border-b-2 border-primary-500' 
                          : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800/50'
                      }`}
                      onClick={() => setActiveProductId(productId)}
                    >
                      {getProductNameById(productId)}
                    </button>
                  ))}
                </div>
                
                {/* Active product keywords section */}
                {activeProductId && (
                  <div className="space-y-4">
                    <div className="mb-4">
                      <div className="flex items-end space-x-2">
                        <div className="flex-grow">
                          <label className="block text-sm font-medium mb-1 text-gray-300">Keyword</label>
                          <input
                            type="text"
                            value={newKeyword}
                            onChange={(e) => setNewKeyword(e.target.value)}
                            className="w-full bg-gray-800/70 border border-gray-700 rounded-md px-3 py-2.5 text-sm h-10 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder="Enter keyword"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter' && newKeyword.trim()) {
                                e.preventDefault();
                                handleAddKeyword(activeProductId);
                              }
                            }}
                          />
                        </div>
                        <div className="w-32">
                          <label className="block text-sm font-medium mb-1 text-gray-300">Condition</label>
                          <select
                            value={newCondition}
                            onChange={(e) => setNewCondition(e.target.value as 'NONE' | 'AND' | 'OR')}
                            className="w-full bg-gray-800/70 border border-gray-700 rounded-md px-3 py-2.5 text-sm h-10 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          >
                            <option value="NONE">None</option>
                            <option value="AND">AND</option>
                            <option value="OR">OR</option>
                          </select>
                        </div>
                        <div className="flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => handleAddKeyword(activeProductId)}
                            className="h-10 px-4 bg-primary hover:bg-primary-dark text-white rounded-md transition-colors flex items-center"
                          >
                            <PlusCircle size={16} className="mr-1" />
                            Add
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Display existing keywords for the active product */}
                    {productKeywords[activeProductId] && productKeywords[activeProductId].length > 0 ? (
                      <>
                        <div className="space-y-2">
                          {productKeywords[activeProductId].map((kw, index) => (
                            <div key={index} className="flex items-center justify-between bg-gray-800/50 p-3 rounded-md">
                              <div className="flex items-center">
                                <span className="font-medium text-gray-300">{kw.keyword}</span>
                                {index > 0 && (
                                  <span className="ml-2 text-text-muted px-2 py-0.5 bg-primary-700/20 rounded-full text-xs">
                                    {kw.condition}
                                  </span>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={() => handleRemoveKeyword(activeProductId, index)}
                                className="text-red-400 hover:text-red-300"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          ))}
                        </div>
                        
                        {/* Condition Preview Section */}
                        <div className="mt-4 p-4 bg-gray-900/50 border border-gray-700 rounded-md">
                          <h4 className="text-sm font-medium text-gray-300 mb-2 flex items-center">
                            <Info size={14} className="mr-1.5 text-primary-400" />
                            Condition Preview
                          </h4>
                          <div className="bg-gray-800/70 p-3 rounded border border-gray-700 overflow-x-auto">
                            <code className="text-sm text-primary-300 font-mono whitespace-pre-wrap break-all">
                              {generateConditionPreview(activeProductId)}
                            </code>
                          </div>
                          <p className="text-xs text-gray-400 mt-2">
                            This is how your condition will be structured in the log filtering logic.
                          </p>
                        </div>
                      </>
                    ) : (
                      <div className="text-gray-400 text-center py-4">
                        No keywords added for this product. Keywords will be used to filter logs.
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Add click outside handler for the products dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      
      // Check if click is outside both the dropdown trigger and the dropdown menu
      const isClickInsideTrigger = dropdownRef.current?.contains(target);
      const isClickInsideDropdown = target.closest('.dropdown-container');
      
      if (isProductsDropdownOpen && !isClickInsideTrigger && !isClickInsideDropdown) {
        setIsProductsDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProductsDropdownOpen]);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4 overflow-hidden" 
      onClick={onClose}
    >
      <div 
        className="bg-background-card rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto animate-modalOpen"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border-dark sticky top-0 bg-background-card z-10">
          <div className="flex items-center">
            <div className="h-12 w-12 mr-3 flex items-center justify-center bg-primary/20 rounded">
              <span className="text-lg font-bold text-primary">
                {product.name ? product.name.substring(0, 2).toUpperCase() : 'LC'}
                </span>
              </div>
            <div>
              <h2 className="text-xl font-semibold">Logger Configuration</h2>
              <div className="flex items-center text-sm text-text-muted-dark">
                Configure Logger Products for {organization.name}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button 
              onClick={onClose}
              className="p-2 rounded-full hover:bg-background-hover text-gray-400 hover:text-gray-200 transition-colors"
              aria-label="Close"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
        {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 size={32} className="animate-spin text-primary mb-4" />
              <p className="text-text-muted-dark">Loading logger configuration...</p>
          </div>
        ) : error ? (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertTriangle size={32} className="text-status-danger mb-4" />
              <p className="text-status-danger mb-4">{error}</p>
            <button 
              onClick={onClose}
                className="premium-button-outline"
            >
              Close
            </button>
          </div>
        ) : (
            <>
              {/* Logger Products Section - Moved to top */}
              {renderLoggerProductsSection()}
              
              {/* Customer Information Section */}
              {renderCustomerInformationSection()}
              
              {/* TLS Certificate Configuration Section */}
              {renderTlsCertificateSection()}
              
              {/* Keywords And/Or Condition Block Section */}
              {renderKeywordsSection()}
              
              {/* Device IDs Section */}
              {renderDeviceIdsSection()}
              
              {/* Actions */}
              <div className="flex justify-between mt-8">
                <button 
                  type="button"
                  onClick={handleDownloadConfig}
                  disabled={isDownloading || !existingCustomer}
                  className={`flex items-center px-4 py-2 rounded-md text-white ${
                    existingCustomer ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-600 cursor-not-allowed'
                  }`}
                >
                  {isDownloading ? (
                    <Loader2 size={16} className="animate-spin mr-2" />
                  ) : (
                    <Download size={16} className="mr-2" />
                  )}
                  Download Config
                </button>
                
                <div className="flex space-x-3">
                  <button 
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 border border-gray-600 text-gray-300 rounded-md hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button 
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="premium-button"
                  >
                    {isSubmitting ? (
                      <Loader2 size={16} className="animate-spin mr-2" />
                    ) : (
                      <Check size={16} className="mr-2" />
                    )}
                    Save Configuration
                  </button>
                </div>
              </div>
            </>
        )}
        </div>
      </div>
      {renderDownloadModal()}
    </div>
  );
};

export default LoggerConfigModal; 